import { useEffect, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { diagnostics, expandDay, type ExpandedDay } from "@/lib/diagnostics";
import {
  DIAGNOSTICS_QR_RENDER_PX,
  MAINTENANCE_OPEN_GUARD_MS,
  MAINTENANCE_TIMEOUT_MS,
} from "@/lib/diagnostics/config";

interface Props {
  onClose: () => void;
}

export default function DebugDiagnosticsPanel({ onClose }: Props) {
  const openedAtRef = useRef(performance.now());

  // QR はパネルを開いた時点で固定する。秒をまたいで gid が変わると reassemble が失敗するため。
  const [qr] = useState(() => {
    const start = performance.now();
    const result = diagnostics.getQRParts();
    const buildMs = performance.now() - start;
    return { ...result, buildMs };
  });

  // 描画サイズは開いた時点のビューポートで固定 (キオスクは解像度不変。リサイズ追従は不要)。
  const [qrPx] = useState(() =>
    Math.round(
      Math.min(DIAGNOSTICS_QR_RENDER_PX, Math.min(window.innerWidth, window.innerHeight) * 0.7),
    ),
  );

  const [snapshot, setSnapshot] = useState(() => diagnostics.getSnapshot());
  const [liveEvents, setLiveEvents] = useState(() => diagnostics.getLiveEvents());
  const [liveErrors, setLiveErrors] = useState(() => diagnostics.getLiveErrors());

  useEffect(() => {
    const update = () => {
      setSnapshot(diagnostics.getSnapshot());
      setLiveEvents(diagnostics.getLiveEvents());
      setLiveErrors(diagnostics.getLiveErrors());
    };
    const interval = setInterval(update, 1000);
    const unsubscribe = diagnostics.subscribe(update);
    return () => {
      clearInterval(interval);
      unsubscribe();
    };
  }, []);

  // 無操作で自動クローズ (キオスクで出しっぱなし防止)。操作のたびにタイマーを引き直す。
  useEffect(() => {
    let timer = setTimeout(onClose, MAINTENANCE_TIMEOUT_MS);
    const rearm = () => {
      clearTimeout(timer);
      timer = setTimeout(onClose, MAINTENANCE_TIMEOUT_MS);
    };
    window.addEventListener("pointerdown", rearm, { capture: true, passive: true });
    return () => {
      clearTimeout(timer);
      window.removeEventListener("pointerdown", rearm, { capture: true });
    };
  }, [onClose]);

  const { parts, partCount, totalBytes, sizes, report, buildMs } = qr;
  const trimSummary = [
    report.droppedDays ? `-${report.droppedDays}d` : "",
    report.strippedStacks ? "nostack" : "",
    report.droppedErrors ? `-${report.droppedErrors}err` : "",
  ]
    .filter(Boolean)
    .join(" ");
  const route = diagnostics.metricSummary("routeMs");
  const render = diagnostics.metricSummary("renderMs");
  const days = snapshot.u.days.map(expandDay).filter((d): d is ExpandedDay => d !== null);
  const todayKey = days.length > 0 ? days[days.length - 1].date : "";

  const handleReset = () => {
    if (!window.confirm("回収済みとしてすべての診断データを削除しますか？")) return;
    if (!window.confirm("この操作は取り消せません。本当に削除しますか？")) return;
    diagnostics.reset();
  };

  return (
    <div
      data-scr="diag"
      className="pointer-events-auto fixed inset-0 z-100 flex items-start justify-center overflow-auto bg-black/50 p-4"
      onClick={() => {
        // タッチ端末は開いた瞬間の合成 click が遅延発火し、この overlay を誤って閉じることがある。
        if (performance.now() - openedAtRef.current < MAINTENANCE_OPEN_GUARD_MS) return;
        onClose();
      }}
    >
      <div
        className="mt-8 flex max-w-[calc(100vw-2rem)] flex-col gap-4 rounded-lg bg-white p-6 shadow-xl ring-1 ring-black/10"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Diagnostics"
      >
        <div className="flex items-center justify-between">
          <h2 className="font-mono text-sm font-bold text-gray-800">
            {snapshot.s} / {snapshot.id.hash}
          </h2>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleReset}
              className="rounded border border-red-300 px-3 py-1 text-xs text-red-700 hover:bg-red-50"
            >
              reset (collected)
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded border border-gray-300 px-3 py-1 text-xs text-gray-700 hover:bg-gray-100"
            >
              close
            </button>
          </div>
        </div>

        {/* QRが640px級になったため、要約列はQR横に十分な幅 (min-w) が残るときだけ並び、
            なければ下へ折り返す。あふれた分はオーバーレイのスクロールで逃す。 */}
        <div className="flex flex-wrap items-start justify-center gap-3">
          <div className="flex flex-col items-center gap-1">
            {/* 連続QR: 2枚以上のときはグリッドで全枚数を同時表示する。順不同で全部スキャンすれば再構成可能。
                marginSize=4: 読み取り側がファインダパターンを検出するための quiet zone (規格上の推奨)。 */}
            <div className="flex flex-wrap items-start justify-center gap-2">
              {parts.map((part, i) => (
                <div key={i} className="flex flex-col items-center gap-0.5">
                  <QRCodeSVG value={part} size={qrPx} level="M" marginSize={4} />
                  {partCount > 1 && (
                    <div className="font-mono text-[10px] font-bold text-gray-700">
                      {i + 1}/{partCount}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="font-mono text-[10px] text-gray-500">
              {totalBytes} bytes
              {partCount > 1 ? ` / ${partCount} QR` : ""}
              {report.trimmed ? ` (trimmed: ${trimSummary})` : ""}
            </div>
          </div>

          <div className="min-w-72 max-w-full flex-1 font-mono text-[11px] leading-tight text-gray-800">
            <Row k="hash" v={snapshot.id.hash} />
            <Row k="built" v={snapshot.id.built} />
            <Row k="boot" v={snapshot.id.boot} />
            <Row k="svcBoot" v={snapshot.id.svcBoot ?? "-"} />
            <Row k="uptime" v={`${snapshot.h.up}s`} />
            <Row
              k="touch"
              v={`${snapshot.h.silent ? "SILENT" : "ok"} / last ${
                snapshot.h.lastTouchMin === null ? "-" : `${snapshot.h.lastTouchMin}m ago`
              }`}
            />
            <Row k="mem" v={snapshot.h.mem !== undefined ? `${snapshot.h.mem} MB` : "-"} />
            <Row k="route" v={route ? `avg ${route.avg}ms / p95 ${route.p95}ms` : "no samples"} />
            <Row
              k="render"
              v={render ? `avg ${render.avg}ms / p95 ${render.p95}ms` : "no samples"}
            />
            {/* 区画別サイズ (RQ1 図表素材): id=識別 h=健全性 u=利用統計 e=エラー */}
            <Row k="sizes" v={`id ${sizes.id} / h ${sizes.h} / u ${sizes.u} / e ${sizes.e} B`} />
            <Row k="payload" v={`${totalBytes} B / ${partCount} QR`} />
            <Row k="qrBuild" v={`${buildMs.toFixed(1)} ms`} />
          </div>
        </div>

        <DailyTable days={days} todayKey={todayKey} />

        <details className="text-xs">
          <summary className="cursor-pointer text-gray-700">
            live events (パネル専用・QR非搭載)
          </summary>
          <ul className="mt-2 max-h-40 overflow-auto font-mono text-[10px] text-gray-700">
            {liveEvents.length === 0 && <li className="text-gray-400">(empty)</li>}
            {liveEvents
              .slice()
              .reverse()
              .map((e, i) => (
                <li key={`${e.t}-${i}`} className="truncate">
                  [{e.t}] {e.type}
                  {e.target ? ` → ${e.target}` : ""}
                </li>
              ))}
          </ul>
        </details>

        <details className="text-xs">
          <summary className="cursor-pointer text-gray-700">live errors</summary>
          <ul className="mt-2 max-h-40 overflow-auto font-mono text-[10px] text-red-700">
            {liveErrors.length === 0 && <li className="text-gray-400">(empty)</li>}
            {liveErrors
              .slice()
              .reverse()
              .map((e, i) => (
                <li key={`${e.t}-${i}`} className="truncate">
                  [{e.t}] {e.type}: {e.target ?? ""}
                </li>
              ))}
          </ul>
        </details>

        <details className="text-xs">
          <summary className="cursor-pointer text-gray-700">raw payload</summary>
          <pre className="mt-2 max-h-60 overflow-auto rounded bg-gray-50 p-2 font-mono text-[10px] text-gray-700">
            {JSON.stringify(snapshot, null, 2)}
          </pre>
        </details>
      </div>
    </div>
  );
}

function DailyTable({ days, todayKey }: { days: ExpandedDay[]; todayKey: string }) {
  if (days.length === 0) {
    return <div className="text-xs text-gray-400">(no daily data yet)</div>;
  }
  const rows = [...days].reverse();
  return (
    <details className="text-xs" open>
      <summary className="cursor-pointer text-gray-700">daily ({days.length})</summary>
      <div className="mt-2 overflow-auto">
        <table className="w-full font-mono text-[10px]">
          <thead className="text-gray-500">
            <tr className="border-b border-gray-200">
              <th className="py-1 pr-2 text-left">date</th>
              <th className="py-1 pr-2 text-right">taps</th>
              <th className="py-1 pr-2 text-right">sess</th>
              <th className="py-1 pr-2 text-right">srch</th>
              <th className="py-1 pr-2 text-right">err</th>
              <th className="py-1 pr-2 text-left">peak</th>
              <th className="py-1 pr-2 text-right">route avg/max</th>
              <th className="py-1 pr-2 text-right">render avg/max</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((d) => {
              const isToday = d.date === todayKey;
              const searchTotal = Object.values(d.searches).reduce((a, b) => a + b, 0);
              return (
                <tr
                  key={d.date}
                  className={`border-b border-gray-100 ${isToday ? "bg-yellow-50" : ""}`}
                >
                  <td className="py-1 pr-2">
                    {d.date}
                    {isToday ? " *" : ""}
                  </td>
                  <td className="py-1 pr-2 text-right">{d.taps}</td>
                  <td className="py-1 pr-2 text-right">{d.sessions}</td>
                  <td className="py-1 pr-2 text-right">{searchTotal}</td>
                  <td className="py-1 pr-2 text-right">{d.errors}</td>
                  <td className="py-1 pr-2">{peakHourLabel(d.hourly)}</td>
                  <td className="py-1 pr-2 text-right">{fmtMetric(d.route)}</td>
                  <td className="py-1 pr-2 text-right">{fmtMetric(d.render)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </details>
  );
}

function peakHourLabel(hourly: number[]): string {
  const max = Math.max(0, ...hourly);
  if (max === 0) return "-";
  return `${hourly.indexOf(max)}時 (${max})`;
}

function fmtMetric([count, sumMs, maxMs]: [number, number, number]): string {
  if (count === 0) return "-";
  return `${Math.round(sumMs / count)}/${maxMs}`;
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex gap-2">
      <span className="w-20 shrink-0 text-gray-500">{k}</span>
      <span className="flex-1 truncate">{v}</span>
    </div>
  );
}
